import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
import { updateShowOtaCreateBackupModal } from "@src/reducers/your-reducer";
import { createProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/create-process-use-case";

export const useOtaCreateBackupModal = (onClose: () => void) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isAskForApprovalLoading, setIsAskForApprovalLoading] =
    useState<boolean>(false);
  const [formValues, setFormValues] = useState<{
    targetPath: string;
    username: string;
    password: string;
    privateKeyFile: File | null;
  }>({
    targetPath: "",
    username: "",
    password: "",
    privateKeyFile: null,
  });

  const texts = {
    closeCreateBackupModalAriaLabel: i18n(
      "modules.otaCreateBackupModal.closeCreateBackupModalAriaLabel",
    ),
    title: i18n("modules.otaCreateBackupModal.title"),
    targetPathLabel: i18n("modules.otaCreateBackupModal.targetPathLabel"),
    targetPathPlaceholder: i18n(
      "modules.otaCreateBackupModal.targetPathPlaceholder",
    ),
    sourceMachineLabel: i18n("modules.otaCreateBackupModal.sourceMachineLabel"),
    usernameLabel: i18n("modules.otaCreateBackupModal.usernameLabel"),
    usernamePlaceholder: i18n(
      "modules.otaCreateBackupModal.usernamePlaceholder",
    ),
    passwordLabel: i18n("modules.otaCreateBackupModal.passwordLabel"),
    passwordPlaceholder: i18n(
      "modules.otaCreateBackupModal.passwordPlaceholder",
    ),
    privateKeyFileLabel: i18n(
      "modules.otaCreateBackupModal.privateKeyFileLabel",
    ),
    helperText: i18n("modules.otaCreateBackupModal.helperText"),
    cancelButtonText: i18n("modules.otaCreateBackupModal.cancelButtonText"),
    askForApprovalButtonText: i18n(
      "modules.otaCreateBackupModal.askForApprovalButtonText",
    ),
    showPasswordTooltip: i18n(
      "modules.otaCreateBackupModal.showPasswordTooltip",
    ),
    hidePasswordTooltip: i18n(
      "modules.otaCreateBackupModal.hidePasswordTooltip",
    ),
    pleaseFixFields: i18n("modules.otaCreateBackupModal.pleaseFixFields"),
    failedToCreateProcess: i18n(
      "modules.otaCreateBackupModal.failedToCreateProcess",
    ),
    processAlreadyExists: i18n(
      "modules.otaCreateBackupModal.processAlreadyExists",
    ),
  };

  const sourceMachineName = source?.name;
  const sourceMachineIp = source?.dsn;

  const handleCloseModal = () => {
    onClose();
    dispatch(updateShowOtaCreateBackupModal(false));
  };

  const handleTargetPathChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((previousState) => ({
      ...previousState,
      targetPath: event.target.value,
    }));
  };

  const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((previousState) => ({
      ...previousState,
      username: event.target.value,
    }));
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((previousState) => ({
      ...previousState,
      password: event.target.value,
    }));
  };

  const handlePrivateKeyFileSelection = (file: File | null) => {
    setFormValues((previousState) => ({
      ...previousState,
      privateKeyFile: file,
    }));
  };

  const handleTogglePasswordVisibility = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setIsPasswordVisible((previousState) => !previousState);
  };

  const handleAskForApproval = async () => {
    if (isAskForApprovalLoading) {
      return;
    }

    const missingFields: string[] = [];

    if (formValues.targetPath.trim() === "") {
      missingFields.push(texts.targetPathLabel);
    }

    if (formValues.username.trim() === "") {
      missingFields.push(texts.usernameLabel);
    }

    if (missingFields.length > 0) {
      warningSnackbar(
        `${texts.pleaseFixFields}: ${missingFields.join(", ")}`,
        5000,
      );
      return;
    }

    setIsAskForApprovalLoading(true);

    try {
      const formData = new FormData();
      formData.append("type", "backup");
      formData.append("delivery_type", "SFTP");
      formData.append("target_path", formValues.targetPath);
      formData.append("ssh_username", formValues.username);

      if (formValues.password.trim() !== "") {
        formData.append("ssh_password", formValues.password);
      }

      if (formValues.privateKeyFile) {
        formData.append("private_key", formValues.privateKeyFile);
      }

      const result = await createProcessUseCase(formData);

      if (result.success) {
        handleCloseModal();
        return;
      }

      if (result.statusCode === 409) {
        warningSnackbar(texts.processAlreadyExists, 5000);
        setIsAskForApprovalLoading(false);
        return;
      }

      warningSnackbar(result.error || texts.failedToCreateProcess, 5000);
      setIsAskForApprovalLoading(false);
    } catch {
      warningSnackbar(texts.failedToCreateProcess, 5000);
      setIsAskForApprovalLoading(false);
    }
  };

  return {
    texts,
    targetPath: formValues.targetPath,
    sourceMachineName,
    sourceMachineIp,
    username: formValues.username,
    password: formValues.password,
    privateKeyFile: formValues.privateKeyFile,
    isPasswordVisible,
    isAskForApprovalLoading,
    handleCloseModal,
    handleTargetPathChange,
    handleUsernameChange,
    handlePasswordChange,
    handlePrivateKeyFileSelection,
    handleTogglePasswordVisibility,
    handleAskForApproval,
  };
};
